import { useState, useEffect, useMemo, useCallback, lazy, Suspense } from 'react'
import {
  Eye,
  BadgeDollarSign,
  MousePointerClick,
  Percent,
  UserRound,
  Target,
  Calendar,
  Home,
  FileText,
  Upload,
  Zap,
  History,
  Video,
  Lock,
  LogOut,
  Users,
  DollarSign,
  Briefcase,
  Megaphone,
  Settings,
  TrendingUp,
  Newspaper,
  Code,
  MessageCircle,
  Mail
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Calendar as CalendarComponent } from "@/components/ui/calendar"
import { format, addDays } from "date-fns";
import { ptBR } from "date-fns/locale"
import Newsletters from './lib/Newsletters/NewslettersCards'
import DailyMetricsChart from './lib/AllMetricsChart.tsx'
import LoadingSpinner from './components/LoadingComponent'
import { ThemeProvider, ThemeToggle } from "@/lib/ThemeProvider"
import { getDateRange } from './lib/utils/date'
import LoginForm from './lib/utils/LoginForm.tsx'
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { toast, Toaster } from 'sonner'
import React from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
// import { FloatingPaths } from './components/ui/BackgroundPath.tsx';
import { QueryProvider } from './providers/QueryProvider'
import { AudioPlayer } from './components/AudioPlayer'
import { SmartSearch } from './components/SmartSearch'
import { ProtectedRoute } from './components/ProtectedRoute'

interface User {
  email: string
  isAuthenticated: boolean
  token: string
  role?: string
  brand_name?: string
  permissions?: string[]
}

interface PlatformMetrics {
  spend: number
  conversions: number
  clicks: number
  impressions: number
}

interface Metrics {
  [platform: string]: PlatformMetrics
}

const MetricsLoadingSpinner = () => (
  <div className="flex items-center justify-center h-16">
    <div className="w-5 h-5 border-2 border-t-[#FF3366] rounded-full animate-spin" />
  </div>
)

const Sidebar = React.memo(({ totalMetrics, user }: { totalMetrics: PlatformMetrics, user: User | null }) => {
  const [openMenus, setOpenMenus] = useState<{ [key: string]: boolean }>({
    marketing: false,
    partnerships: false,
    settings: false
  });

  const toggleMenu = (menu: string) => {
    setOpenMenus(prev => ({
      ...prev,
      [menu]: !prev[menu]
    }));
  };

  const menuItems = useMemo(() => ({
    main: [
      { value: "", icon: Home, label: "Dashboard", roles: ["ADMIN", "MARKETING", "BRAND", "FINANCE"] }
    ],
    marketing: [
      { 
        value: "marketing", 
        icon: TrendingUp, 
        label: "Métricas", 
        permissions: ["metrics.view", "metrics.edit"] 
      },
      { 
        value: "referrals", 
        icon: Users, 
        label: "Referências", 
        permissions: ["metrics.view"] 
      },
      { 
        value: "thenews", 
        icon: FileText, 
        label: "Cadernos", 
        permissions: ["newsletters.view", "newsletters.edit"] 
      },
      { 
        value: "news", 
        icon: Newspaper, 
        label: "Feed de Notícias", 
        permissions: ["insights.view", "insights.edit"] 
      },
      { 
        value: "upload", 
        icon: Upload, 
        label: "Upload", 
        permissions: ["metrics.edit"] 
      },
      { 
        value: "streaks", 
        icon: Zap, 
        label: "Streaks", 
        permissions: ["metrics.view"] 
      }
    ],
    referrals: [
      { 
        value: "referrals", 
        icon: Users, 
        label: "Referências", 
        permissions: ["metrics.view"] 
      },
      { 
        value: "delivery-forms", 
        icon: Users, 
        label: "Formulários de Entrega", 
        permissions: ["metrics.view"] 
      }
    ],
    CRM: [
      { 
        value: "crm", 
        icon: MessageCircle, 
        label: "WhatsApp", 
        permissions: ["crm.view", "crm.edit"] 
      },
      { 
        value: "crm/support", 
        icon: Mail, 
        label: "Suporte", 
        permissions: ["support.edit"] 
      },
    ],
    partnerships: [
      { 
        value: "vendas", 
        icon: DollarSign, 
        label: "Gerenciar Campanhas", 
        permissions: ["campaigns.view", "campaigns.edit"] 
      },
      {
        value: "urlshortener",
        icon: Link,
        label: "URL Shortener",
        permissions: []
      }
    ],
    tecnologia: [
      { 
        value: "tecnologia", 
        icon: Code, 
        label: "Tickets", 
        permissions: ["tech.view"] 
      }
    ],
    settings: [
      { 
        value: "users", 
        icon: Users, 
        label: "Usuários", 
        permissions: ["users.manage"] 
      }
    ],
    bottom: [
      { 
        value: "changelogs", 
        icon: History, 
        label: "Changelogs", 
        permissions: []
      }
    ]
  }), []);

  const hasPermission = (requiredPermissions: string[]) => {
    if (!requiredPermissions || requiredPermissions.length === 0) return true;
    
    if (user?.permissions?.includes('*')) return true;
    
    // Verifica se o usuário tem qualquer uma das permissões necessárias
    return requiredPermissions.some(permission => {
      // Se a permissão for do CRM, verifica se tem qualquer permissão relacionada ao CRM
      if (permission.startsWith('crm.') || permission.startsWith('support.')) {
        return user?.permissions?.some(userPermission => 
          userPermission.startsWith('crm.') || userPermission.startsWith('support.')
        );
      }
      return user?.permissions?.includes(permission);
    });
  };

  const renderMenuItem = (item: any) => {
    if (!hasPermission(item.permissions)) return null;

    return (
      <Link
        key={item.value}
        to={`/${item.value === 'overview' ? '' : item.value}`}
        className="block"
      >
        <Button
          variant="ghost"
          className="w-full justify-start gap-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <item.icon className="h-5 w-5" />
          {item.label}
        </Button>
      </Link>
    );
  };

  const renderSubmenu = (title: string, icon: any, items: any[]) => {
    const accessibleItems = items.filter(item => hasPermission(item.permissions));
    
    if (accessibleItems.length === 0) return null;

    return (
      <div key={title} className="space-y-1">
        <Button
          variant="ghost"
          className="w-full justify-between gap-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700"
          onClick={() => toggleMenu(title.toLowerCase())}
        >
          <div className="flex items-center gap-2">
            {icon && React.createElement(icon, { className: "h-5 w-5" })}
            {title}
          </div>
          <svg
            className={`h-4 w-4 transform transition-transform ${openMenus[title.toLowerCase()] ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </Button>
        {openMenus[title.toLowerCase()] && (
          <div className="pl-6 space-y-1">
            {accessibleItems.map(item => renderMenuItem(item))}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside className="w-64 bg-white dark:bg-gray-800 border-r dark:border-gray-700 flex flex-col">
      <div className="p-4">
        <img
          src="https://dash.testeswaffle.org/logo.png"
          alt="Dashboard Logo"
          className="h-8"
        />
      </div>
      <nav className="flex-1 space-y-1 px-2">
        {menuItems.main.map(item => renderMenuItem(item))}
        {renderSubmenu("Marketing", Megaphone, menuItems.marketing)}
        {renderSubmenu("Partnerships", Briefcase, menuItems.partnerships)}
        {renderSubmenu("Tecnologia", Code, menuItems.tecnologia)}
        {renderSubmenu("Referrals", Users, menuItems.referrals)}
        {renderSubmenu("CRM", UserRound, menuItems.CRM)}
        {renderSubmenu("Settings", Settings, menuItems.settings)}
      </nav>

      <div className="p-4">
        <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4">
          <h3 className="text-sm font-medium mb-2 text-gray-900 dark:text-gray-100">Total Investido</h3>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            R$ {totalMetrics.spend.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>

        <div className="my-4 relative">
          <div className="absolute inset-0 flex items-center" aria-hidden="true">
            <div className="w-full border-t border-gray-300 dark:border-gray-600 opacity-50"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="px-2 bg-white dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400">
              Cadernos
            </span>
          </div>
        </div>

        <Newsletters />
      </div>

      <div className="px-2 py-4 border-t dark:border-gray-700">
        {menuItems.bottom.map(item => renderMenuItem(item))}
      </div>
    </aside>
  );
});

const Header = React.memo(({ totalMetrics, onLogout, user, DateRangePicker, metrics }: {
  totalMetrics: PlatformMetrics,
  onLogout: () => void,
  user: User | null,
  DateRangePicker: () => JSX.Element,
  metrics: { [platform: string]: PlatformMetrics }
}) => {
  return (
    <header className="bg-white dark:bg-gray-800 border-b dark:border-gray-700">
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-4 w-[600px]">
          <SmartSearch metrics={metrics} />
        </div>
        <div className="flex items-center gap-4">
          <DateRangePicker />
          <div className="flex items-center gap-2">
            <span className="text-green-500">CTR</span>
            <span className="text-green-500">+{totalMetrics.conversions > 0 ? ((totalMetrics.clicks / totalMetrics.impressions) * 100).toFixed(2) : '0'}%</span>
          </div>
          <ThemeToggle />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative flex items-center gap-2 p-2">
                <div className="relative">
                  <div className="w-8 h-8 rounded-full bg-[#FF3366] flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {user?.email?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white dark:border-gray-800" />
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72 mt-2 p-2">
              <div className="flex items-center gap-3 p-2 mb-2">
                <div className="w-10 h-10 rounded-full bg-[#FF3366] flex items-center justify-center">
                  <span className="text-white text-lg font-medium">
                    {user?.email?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-medium dark:text-white">
                    {user?.email?.split('@')[0]}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {user?.role || 'Usuário'}
                  </span>
                </div>
              </div>

              <DropdownMenuSeparator className="my-2" />

              <DropdownMenuItem className="p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md">
                <UserRound className="mr-2 h-4 w-4" />
                <span>Perfil</span>
              </DropdownMenuItem>

              <DropdownMenuItem
                className="p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                onClick={() => window.open('https://app.convenia.com.br', '_blank')}
              >
                <FileText className="mr-2 h-4 w-4" />
                <span>Convenia</span>
              </DropdownMenuItem>

              <DropdownMenuItem
                className="p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                onClick={() => window.open('https://meet.google.com', '_blank')}
              >
                <Video className="mr-2 h-4 w-4" />
                <span>Meetings</span>
              </DropdownMenuItem>

              <DropdownMenuItem
                className="p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                onClick={() => window.location.href = '/password'}
              >
                <Lock className="mr-2 h-4 w-4" />
                <span>Alterar Senha</span>
              </DropdownMenuItem>

              <DropdownMenuSeparator className="my-2" />

              <DropdownMenuItem
                onClick={onLogout}
                className="p-2 cursor-pointer text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-md"
              >
                <LogOut className="mr-2 h-4 w-4" />
                <span>Sair</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
});

// Lazy loading dos componentes
const CustomDashboard = lazy(() => import('./pages/Marketing/CustomDashboard'));
const NewsletterInsights = lazy(() => import('./lib/Newsletters/NewsletterAnalysis'));
const NewsFeed = lazy(() => import('./pages/Conteudo/News'));
const UploadAnalysis = lazy(() => import('./lib/csvUpload'));
const StreaksDashboard = lazy(() => import('./components/Streaks/Dashboard'));
const Changelog = lazy(() => import('./lib/Updates'));
const PasswordChange = lazy(() => import('./lib/PasswordChange'));
const UserAdmin = lazy(() => import('./pages/UserAdmin').then(module => ({ default: module.UserAdmin })));
const BrandCampaigns = lazy(() => import('./pages/Vendas/BrandCampaigns').then(module => ({ default: module.BrandCampaigns })));
const TecnologiaPage = lazy(() => import('./pages/Tecnologia/TecnologiaPage'));
const CRMPage = lazy(() => import('./pages/CRM'));
const Support = lazy(() => import('./pages/CRM/Support'));
const URLShortener = lazy(() => import('./pages/Vendas/URLShortener'));
const Referrals = lazy(() => import('./pages/Referrals/Referrals'));
const DeliveryForms = lazy(() => import('./pages/Referrals/DeliveryForms'));

export default function Component() {
  const [user, setUser] = useState<User | null>(() => {
    if (typeof window !== 'undefined') {
      const storedUser = localStorage.getItem('user')
      if (storedUser) {
        try {
          return JSON.parse(storedUser)
        } catch {
          return null
        }
      }
    }
    return null
  })
  const [pageLoading, _setPageLoading] = useState(false)
  const [metricsLoading, setMetricsLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [selectedPeriod, setSelectedPeriod] = useState('7d')
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [dateRange, setDateRange] = useState({
    from: undefined,
    to: undefined,
  })
  const [isCustomDate, setIsCustomDate] = useState(false)
  const [metrics, setMetrics] = useState<Metrics>({})
  const [googleError, setGoogleError] = useState(false);
  const [_magicLinkSent, setMagicLinkSent] = useState(false)

  const DateRangePicker = () => {
    const handleSelect = (range: any) => {
      setDateRange(range)
      if (range.from && range.to) {
        setIsCalendarOpen(false)
        fetchMetrics()
      }
    }

    const handlePeriodChange = (value: string) => {
      setSelectedPeriod(value)
      setIsCustomDate(value === 'custom')
      if (value !== 'custom') {
        setDateRange({ from: undefined, to: undefined })
        setTimeout(() => {
          fetchMetrics()
        }, 0)
      }
    }

    return (
      <div className="flex items-center gap-2">
        <Select
          value={selectedPeriod}
          onValueChange={handlePeriodChange}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Selecione o período" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="24h">Hoje</SelectItem>
            <SelectItem value="yesterday">Ontem</SelectItem>
            <SelectItem value="7d">Últimos 7 dias</SelectItem>
            <SelectItem value="14d">Últimos 14 dias</SelectItem>
            <SelectItem value="30d">Últimos 30 dias</SelectItem>
            <SelectItem value="thisWeek">Esta semana</SelectItem>
            <SelectItem value="lastWeek">Semana passada</SelectItem>
            <SelectItem value="thisMonth">Este mês</SelectItem>
            <SelectItem value="lastMonth">Mês passado</SelectItem>
            <SelectItem value="maximum">Máximo (365 dias)</SelectItem>
            <SelectItem value="custom">Personalizado</SelectItem>
          </SelectContent>
        </Select>

        {isCustomDate && (
          <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={`w-[280px] justify-start text-left font-normal ${!dateRange.from && "text-muted-foreground"
                  }`}
                onClick={() => setIsCalendarOpen(true)}
              >
                <Calendar className="mr-2 h-4 w-4" />
                {dateRange.from ? (
                  dateRange.to ? (
                    <>
                      {format(dateRange.from, "dd/MM/yyyy", { locale: ptBR })} -{" "}
                      {format(dateRange.to, "dd/MM/yyyy", { locale: ptBR })}
                    </>
                  ) : (
                    format(dateRange.from, "dd/MM/yyyy", { locale: ptBR })
                  )
                ) : (
                  <span>Selecione um período</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-[#FFFFFF]" align="start">
              <CalendarComponent
                initialFocus
                mode="range"
                defaultMonth={dateRange.from}
                selected={dateRange}
                onSelect={handleSelect}
                numberOfMonths={2}
                locale={ptBR}
                disabled={(date) => date > new Date() || date < addDays(new Date(), -365)}
                fixedWeeks
              />
            </PopoverContent>
          </Popover>
        )}
      </div>
    )
  }

  const fetchMetrics = useCallback(async () => {
    if (!user?.token) return;

    const currentPath = window.location.pathname;
    if (currentPath !== '/marketing') {
      return;
    }

    // Verificar cache global
    const globalCache = localStorage.getItem('metricsCache');
    if (globalCache) {
      const cache = JSON.parse(globalCache);
      const now = Date.now();
      
      // Se o cache global é válido, use-o
      if (now - cache.timestamp < 10 * 60 * 1000) { // 10 minutos
        setMetrics(cache.data);
        return;
      }
    }

    setMetricsLoading(true);
    setGoogleError(false);
    try {
      let startDate, endDate;

      if (isCustomDate && dateRange.from && dateRange.to) {
        startDate = format(dateRange.from, 'yyyy-MM-dd');
        endDate = format(dateRange.to, 'yyyy-MM-dd');
      } else {
        const dates = getDateRange(selectedPeriod);
        if (!dates) return;
        startDate = dates.startDate;
        endDate = dates.endDate;
      }

      const newMetrics: Metrics = {};

      // Buscar dados do Facebook e TikTok primeiro
      for (const platform of ['facebook', 'tiktok']) {
        try {
          const response = await fetch(
            `https://waffle-v2.thenewcc.workers.dev/metrics/${platform}/${startDate}/${endDate}`,
            {
              headers: {
                'Authorization': `Bearer ${user.token}`
              }
            }
          );

          if (response.status === 401 || response.status === 403) {
            localStorage.removeItem('user');
            window.location.reload();
            throw new Error('Sessão expirada. Por favor, faça login novamente.');
          }
          
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          const result = await response.json();
          
          newMetrics[platform] = {
            spend: result?.spend || 0,
            conversions: result?.conversions || 0,
            clicks: result?.clicks || 0,
            impressions: result?.impressions || 0
          };
        } catch (error) {
          console.error(`Erro ao carregar dados do ${platform}:`, error);
          newMetrics[platform] = {
            spend: 0,
            conversions: 0,
            clicks: 0,
            impressions: 0
          };
        }
      }

      // Tentar buscar dados do Google separadamente
      try {
        const googleResponse = await fetch(
          `https://waffle-v2.thenewcc.workers.dev/metrics/google/${startDate}/${endDate}`,
          {
            headers: {
              'Authorization': `Bearer ${user.token}`
            }
          }
        );

        if (googleResponse.status === 401 || googleResponse.status === 403) {
          localStorage.removeItem('user');
          window.location.reload();
          throw new Error('Sessão expirada. Por favor, faça login novamente.');
        }
        
        if (!googleResponse.ok) throw new Error(`HTTP error! status: ${googleResponse.status}`);
        const googleResult = await googleResponse.json();
        
        newMetrics.google = {
          spend: googleResult?.spend || 0,
          conversions: googleResult?.conversions || 0,
          clicks: googleResult?.clicks || 0,
          impressions: googleResult?.impressions || 0
        };
      } catch (error) {
        console.error('Erro ao carregar dados do Google:', error);
        setGoogleError(true);
        newMetrics.google = {
          spend: 0,
          conversions: 0,
          clicks: 0,
          impressions: 0
        };
      }

      // Atualizar cache global
      const cacheData = {
        data: newMetrics,
        timestamp: Date.now()
      };
      localStorage.setItem('metricsCache', JSON.stringify(cacheData));
      localStorage.setItem('currentMetrics', JSON.stringify(newMetrics));

      setMetrics(newMetrics);
    } catch (error) {
      console.error('Erro ao carregar métricas:', error);
      setError('Falha ao carregar dados. Por favor, tente novamente.');
    } finally {
      setMetricsLoading(false);
    }
  }, [user?.token, isCustomDate, dateRange, selectedPeriod]);

  useEffect(() => {
    const storedUser = localStorage.getItem('user')
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser)
        if (parsedUser.token) {
          setUser(parsedUser)
        }
      } catch (error) {
        console.error('Error parsing stored user:', error)
        localStorage.removeItem('user')
      }
    }
  }, [])

  useEffect(() => {
    if (user?.isAuthenticated && user?.token) {
      const currentPath = window.location.pathname;
      
      if (currentPath === '/marketing') {
        fetchMetrics();

        // Configurar intervalo de atualização mais longo (10 minutos)
        const intervalId = setInterval(fetchMetrics, 10 * 60 * 1000);
        return () => clearInterval(intervalId);
      }
    }
  }, [user?.isAuthenticated, user?.token, fetchMetrics]);

  const handleLogin = async (email: string) => {
    setLoading(true)
    setError('')
    setMagicLinkSent(false)

    try {
      const response = await fetch('https://backend.testeswaffle.org/auth/magic-link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao enviar link de acesso')
      }

      setMagicLinkSent(true)
      toast.success("Link de acesso enviado! Verifique seu email para acessar sua conta.")
      setEmail('')
    } catch (err: any) {
      console.error(err)
      toast.error(err.message || 'Erro ao enviar link de acesso. Por favor, tente novamente.')
      setError(err.message || 'Erro ao enviar link de acesso. Por favor, tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const verifyMagicLink = async (token: string) => {
      try {
        const response = await fetch('https://backend.testeswaffle.org/auth/verify-magic-link', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ token })
        })

        const data = await response.json()

        if (!response.ok) {
          throw new Error(data.message || 'Link de acesso inválido')
        }

        const userData = {
          email: data.user.email,
          isAuthenticated: true,
          token: data.token,
          role: data.user.role,
          brand_name: data.user.brand_name,
          permissions: data.user.permissions
        }

        localStorage.setItem('user', JSON.stringify(userData))
        setUser(userData)

        window.location.href = '/'
      } catch (err: any) {
        console.error(err)
        setError(err.message || 'Link de acesso inválido. Por favor, solicite um novo link.')
      }
    }

    const params = new URLSearchParams(window.location.search)
    const token = params.get('token')

    if (token) {
      verifyMagicLink(token)
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  }, [])

  const handleLogout = useCallback(() => {
    setUser(null)
    localStorage.removeItem('user')
    window.location.href = '/'
  }, [])

  const totalMetrics = useMemo(() => {
    return Object.values(metrics).reduce(
      (acc: PlatformMetrics, platformMetrics: PlatformMetrics | undefined) => ({
        spend: acc.spend + (platformMetrics?.spend || 0),
        conversions: acc.conversions + (platformMetrics?.conversions || 0),
        clicks: acc.clicks + (platformMetrics?.clicks || 0),
        impressions: acc.impressions + (platformMetrics?.impressions || 0),
      }),
      { spend: 0, conversions: 0, clicks: 0, impressions: 0 }
    );
  }, [metrics]);

  const cac = useMemo(() =>
    totalMetrics.conversions > 0 ? totalMetrics.spend / totalMetrics.conversions : 0
    , [totalMetrics]);

  const ctr = useMemo(() =>
    totalMetrics.impressions > 0 ? (totalMetrics.clicks / totalMetrics.impressions) * 100 : 0
    , [totalMetrics]);

  if (!user?.isAuthenticated) {
    if (pageLoading) {
      return <LoadingSpinner />
    }

    return (
      <LoginForm
        handleLogin={handleLogin}
        error={error}
        loading={loading}
        email={email}
        setEmail={setEmail}
      />
    )
  }

  return (
    <QueryProvider>
      <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
        <Router>
          <div className="flex h-screen bg-transparent dark:bg-gray-900 relative">
            {/* <div className="absolute inset-0 -z-10">
              <FloatingPaths position={1} />
              <FloatingPaths position={-1} />
            </div> */}
            <Sidebar totalMetrics={totalMetrics} user={user} />
            <div className="flex-1 flex flex-col overflow-hidden">
              <Header
                totalMetrics={totalMetrics}
                onLogout={handleLogout}
                user={user}
                DateRangePicker={DateRangePicker}
                metrics={metrics}
              />
              <AudioPlayer />
              <main className="flex-1 overflow-auto p-6">
                <div className="container max-w-1xl mx-auto">
                  <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                      <Route
                        path="/"
                        element={
                          <ProtectedRoute>
                            <Suspense fallback={<LoadingSpinner />}>
                              <CustomDashboard />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/marketing"
                        element={
                          <ProtectedRoute requiredPermissions={["metrics.view"]}>
                            <Suspense fallback={<LoadingSpinner />}>
                              <>
                                <div className="container max-w-6xl mx-auto">
                                  <div className="grid gap-4 w-full px-4 md:px-0">
                                    {googleError && (
                                      <Alert variant="destructive" className="mb-4">
                                        <AlertDescription>
                                          Não foi possível carregar os dados do Google. Os totais exibidos consideram apenas Meta e TikTok.
                                        </AlertDescription>
                                      </Alert>
                                    )}
                                  </div>
                                  <div className="flex flex-col items-center justify-center">
                                    <span className="pointer-events-none whitespace-pre-wrap bg-gradient-to-b from-[#FF3366] to-[#FF99CC] bg-clip-text text-center text-8xl font-semibold leading-none text-transparent dark:from-[#FF3366] dark:to-[#FF1493]">
                                      Marketing
                                    </span>
                                  </div>
                                  <div className="grid gap-6 w-full px-4 md:px-0 md:grid-cols-2">
                                  </div>
                                  <div className="grid gap-4 w-full px-4 md:px-0 md:grid-cols-4 mb-6 ">
                                    <Card className="col-span-4 shadow-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800  ">
                                      <CardHeader className="pb-2">
                                        <CardTitle className="text-lg font-medium text-gray-900 dark:text-gray-100">Visão Geral</CardTitle>
                                      </CardHeader>
                                      <CardContent>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                          {Object.entries(metrics).map(([platform, data]) => (
                                            <div key={platform} className="p-4 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
                                              <div className="flex items-center justify-between mb-4">
                                                <h3 className="text-base font-medium capitalize text-gray-900 dark:text-gray-100">
                                                  {platform}
                                                </h3>
                                                <div className="flex items-center gap-2">
                                                  <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${data?.conversions > 0 ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100' : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100'
                                                    }`}>
                                                    {((data?.conversions || 0) / (totalMetrics.conversions || 1) * 100).toFixed(1)}%
                                                  </span>
                                                </div>
                                              </div>

                                              <div className="space-y-3">
                                                <div className="flex justify-between items-center">
                                                  <span className="text-sm text-gray-500 dark:text-gray-400">Investimento</span>
                                                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                    R$ {(data?.spend || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                  </span>
                                                </div>

                                                <div className="grid grid-cols-2 gap-2">
                                                  <div className="flex flex-col">
                                                    <span className="text-xs text-gray-500 dark:text-gray-400">Conversões</span>
                                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                      {(data?.conversions || 0).toLocaleString()}
                                                    </span>
                                                  </div>
                                                  <div className="flex flex-col">
                                                    <span className="text-xs text-gray-500 dark:text-gray-400">CAC</span>
                                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                      R$ {(data?.conversions > 0 ? data?.spend / data?.conversions : 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                    </span>
                                                  </div>
                                                  <div className="flex flex-col">
                                                    <span className="text-xs text-gray-500 dark:text-gray-400">CTR</span>
                                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                      {(data?.impressions > 0 ? (data?.clicks / data?.impressions) * 100 : 0).toFixed(2)}%
                                                    </span>
                                                  </div>
                                                  <div className="flex flex-col">
                                                    <span className="text-xs text-gray-500 dark:text-gray-400">Cliques</span>
                                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                      {(data?.clicks || 0).toLocaleString()}
                                                    </span>
                                                  </div>
                                                </div>

                                                <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                                                  <div className="flex items-center justify-between">
                                                    <span className="text-xs text-gray-500 dark:text-gray-400">Performance</span>
                                                    <div className="flex items-center gap-1">
                                                      <div className="h-2 w-24 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                                        <div
                                                          className="h-full bg-[#FF3366] rounded-full"
                                                          style={{
                                                            width: `${((data?.conversions || 0) / (totalMetrics.conversions || 1) * 100)}%`
                                                          }}
                                                        />
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                          <div className="flex items-center space-x-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
                                            <div className="p-2 rounded-full bg-pink-50 dark:bg-pink-900">
                                              <BadgeDollarSign className="h-5 w-5 text-[#FF3366]" />
                                            </div>
                                            <div className="flex-1">
                                              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Gasto</p>
                                              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                                                {metricsLoading ? (
                                                  <MetricsLoadingSpinner />
                                                ) : (
                                                  `R$ ${totalMetrics.spend.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                                                )}
                                              </h3>
                                            </div>
                                          </div>

                                          <div className="flex items-center space-x-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
                                            <div className="p-2 rounded-full bg-pink-50 dark:bg-pink-900">
                                              <Target className="h-5 w-5 text-[#FF3366]" />
                                            </div>
                                            <div className="flex-1">
                                              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Conversões</p>
                                              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                                                {metricsLoading ? (
                                                  <MetricsLoadingSpinner />
                                                ) : (
                                                  totalMetrics.conversions.toLocaleString()
                                                )}
                                              </h3>
                                            </div>
                                          </div>

                                          <div className="flex items-center space-x-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
                                            <div className="p-2 rounded-full bg-pink-50 dark:bg-pink-900">
                                              <UserRound className="h-5 w-5 text-[#FF3366]" />
                                            </div>
                                            <div className="flex-1">
                                              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">CAC</p>
                                              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                                                {metricsLoading ? (
                                                  <MetricsLoadingSpinner />
                                                ) : (
                                                  `R$ ${cac.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                                                )}
                                              </h3>
                                            </div>
                                          </div>

                                          <div className="flex items-center space-x-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
                                            <div className="p-2 rounded-full bg-pink-50 dark:bg-pink-900">
                                              <MousePointerClick className="h-5 w-5 text-[#FF3366]" />
                                            </div>
                                            <div className="flex-1">
                                              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Cliques</p>
                                              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                                                {metricsLoading ? (
                                                  <MetricsLoadingSpinner />
                                                ) : (
                                                  totalMetrics.clicks.toLocaleString()
                                                )}
                                              </h3>
                                            </div>
                                          </div>

                                          <div className="flex items-center space-x-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
                                            <div className="p-2 rounded-full bg-pink-50 dark:bg-pink-900">
                                              <Eye className="h-5 w-5 text-[#FF3366]" />
                                            </div>
                                            <div className="flex-1">
                                              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Impressões</p>
                                              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                                                {metricsLoading ? (
                                                  <MetricsLoadingSpinner />
                                                ) : (
                                                  totalMetrics.impressions.toLocaleString()
                                                )}
                                              </h3>
                                            </div>
                                          </div>

                                          <div className="flex items-center space-x-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
                                            <div className="p-2 rounded-full bg-pink-50 dark:bg-pink-900">
                                              <Percent className="h-5 w-5 text-[#FF3366]" />
                                            </div>
                                            <div className="flex-1">
                                              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">CTR</p>
                                              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                                                {metricsLoading ? (
                                                  <MetricsLoadingSpinner />
                                                ) : (
                                                  `${ctr.toFixed(2)}%`
                                                )}
                                              </h3>
                                            </div>
                                          </div>
                                        </div>
                                      </CardContent>
                                    </Card>
                                  </div>
                                  <DailyMetricsChart />
                                </div>
                              </>
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/thenews"
                        element={
                          <ProtectedRoute requiredPermissions={["newsletters.view"]}>
                            <Suspense fallback={<LoadingSpinner />}>
                              <NewsletterInsights initialCaderno="todos" />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/news"
                        element={
                          <ProtectedRoute requiredPermissions={["insights.view"]}>
                            <Suspense fallback={<LoadingSpinner />}>
                              <NewsFeed />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/upload"
                        element={
                          <ProtectedRoute requiredPermissions={["metrics.edit"]}>
                            <Suspense fallback={<LoadingSpinner />}>
                              <UploadAnalysis />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/streaks"
                        element={
                          <ProtectedRoute requiredPermissions={["metrics.view"]}>
                            <Suspense fallback={<LoadingSpinner />}>
                              <StreaksDashboard />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/changelogs"
                        element={
                          <ProtectedRoute>
                            <Suspense fallback={<LoadingSpinner />}>
                              <Changelog />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/password"
                        element={
                          <ProtectedRoute>
                            <Suspense fallback={<LoadingSpinner />}>
                              <PasswordChange />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/users"
                        element={
                          <ProtectedRoute requiredPermissions={["users.manage"]}>
                            <Suspense fallback={<LoadingSpinner />}>
                              <UserAdmin />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/vendas"
                        element={
                          <ProtectedRoute requiredPermissions={["campaigns.view"]}>
                            <Suspense fallback={<LoadingSpinner />}>
                              <BrandCampaigns />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/tecnologia"
                        element={
                          <ProtectedRoute requiredPermissions={["tech.view"]}>
                            <Suspense fallback={<LoadingSpinner />}>
                              <TecnologiaPage />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/crm"
                        element={
                          <ProtectedRoute requiredPermissions={["crm.view"]}>
                            <Suspense fallback={<LoadingSpinner />}>
                              <CRMPage />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/crm/support"
                        element={
                          <ProtectedRoute requiredPermissions={[]}>
                            <Suspense fallback={<LoadingSpinner />}>
                              <Support />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/urlshortener"
                        element={
                          <ProtectedRoute requiredPermissions={[]}>
                            <Suspense fallback={<LoadingSpinner />}>
                              <URLShortener />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/referrals"
                        element={
                          <ProtectedRoute requiredPermissions={["metrics.view"]}>
                            <Suspense fallback={<LoadingSpinner />}>
                              <Referrals />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/delivery-forms"
                        element={
                          <ProtectedRoute requiredPermissions={["metrics.view"]}>
                            <Suspense fallback={<LoadingSpinner />}>
                              <DeliveryForms /> 
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />
                    </Routes>
                  </Suspense>
                </div>
              </main>
            </div>
          </div>
        </Router>
        <Toaster />
      </ThemeProvider>
    </QueryProvider>
  );
}
